import {
  Component,
  OnInit,
  LOCALE_ID,
  ViewChild,
  AfterViewInit,
  signal,
  inject,
  ViewContainerRef,
  TemplateRef,
} from '@angular/core';
import {
  NZ_MODAL_DATA,
  NzModalModule,
  NzModalRef,
  NzModalService,
} from 'ng-zorro-antd/modal';
import { State } from 'src/app/shared/state/state';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InombraRoutes } from 'src/app/models/routes';
import moment from 'moment';
import { SchedulerService } from 'src/app/shared/services/scheduler.service';
import { DeleteEventModalComponent } from '../delete-modal/delete-modal.component';
import { UploadSources } from 'src/app/modules/private/file-manager/models';
import { Utils } from 'src/app/shared/services/utilities';
import { UntilDestroy } from '@ngneat/until-destroy';
import { NotificationService } from 'src/app/shared/services/notification.service';
import { AddressFormComponent } from 'src/app/shared/components/address-form/address-form.component';
import { complexities, dateTimeFormat } from 'src/app/app.constants';
import { AddressHttpService } from 'src/app/shared/services/http/address-http.service';
import { EventHttpService } from 'src/app/shared/services/http/event-http.service';
import { FileHttpService } from 'src/app/shared/services/http/file-http.service';
import { RecurrenceEditorComponent } from '../recurrence-editor/recurrence-editor.component';
import { CommonModule } from '@angular/common';
import { HasRequiredPipe } from 'src/app/shared/pipes/has-required.pipe';
import { SuggestedSearchPipe } from 'src/app/shared/pipes/suggested-search.pipe';
import { UsernamePipe } from 'src/app/shared/pipes/username.pipe';
import { MomentPipe } from 'src/app/shared/pipes/moment.pipe';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { DueDateTagComponent } from 'src/app/shared/components/due-date-tag/due-date-tag.component';
import { ModuleTagComponent } from 'src/app/shared/components/module-tag/module-tag.component';
import { UploadFilesComponent } from 'src/app/shared/components/upload-files-component/upload-files.component';
import { AvatarComponent } from 'src/app/shared/components/avatar/avatar.component';
import { DatePickerComponent } from 'src/app/shared/components/date-picker/date-picker.component';
import { FieldsetComponent } from 'src/app/shared/components/fieldset/fieldset.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { RouterModule } from '@angular/router';

enum ExtraOptionEnum {
  IsAllDay = 'IsAllDay',
  IsHoliday = 'IsHoliday',
  IsRecurring = 'IsRecurring',
  IsReminder = 'IsReminder',
  IsFixedArrival = 'IsFixedArrival',
  IsAutoDelete = 'IsAutoDelete',
}
type ExtraOption = keyof typeof ExtraOptionEnum;

@UntilDestroy()
@Component({
  selector: 'app-editor-event-modal',
  templateUrl: './editor-modal.component.html',
  styleUrl: './editor-modal.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    HasRequiredPipe,
    SuggestedSearchPipe,
    UsernamePipe,
    MomentPipe,
    NzRadioModule,
    NzSelectModule,
    NzInputModule,
    NzInputNumberModule,
    NzSwitchModule,
    NzFormModule,
    NzModalModule,
    NzPopoverModule,
    NzToolTipModule,
    NzTagModule,
    NzIconModule,
    NzButtonModule,
    NzSpinModule,
    NzAlertModule,
    DragDropModule,
    FieldsetComponent,
    DatePickerComponent,
    AddressFormComponent,
    AvatarComponent,
    RecurrenceEditorComponent,
    UploadFilesComponent,
    ModuleTagComponent,
    DueDateTagComponent,
  ],
})
export class EditorEventModalComponent implements OnInit, AfterViewInit {
  readonly eventHttp = inject(EventHttpService);
  readonly fileHttp = inject(FileHttpService);
  readonly addressHttp = inject(AddressHttpService);
  readonly modalRef = inject(NzModalRef);
  readonly modalService = inject(NzModalService);
  readonly state = inject(State);
  readonly fb = inject(FormBuilder);
  readonly schedulerService = inject(SchedulerService);
  readonly notify = inject(NotificationService);
  readonly utilities = inject(Utils);
  readonly locale = inject(LOCALE_ID);
  readonly nzData = inject<{
    params: any;
    preselectedProject: number;
    hideEventTypeSelection?: boolean;
    disableProjectSelection?: boolean;
  }>(NZ_MODAL_DATA);
  readonly dateTimeFormat = dateTimeFormat;
  @ViewChild('AddressFormReminder') addressFormReminder: AddressFormComponent;
  @ViewChild('AddressFormEvent') addressFormEvent: AddressFormComponent;
  @ViewChild('ReccurenceEditor') ReccurenceEditor: RecurrenceEditorComponent;
  @ViewChild('UploadFilesComponent') UploadFilesComponent: UploadFilesComponent;
  @ViewChild('CalendarComponent', { read: ViewContainerRef })
  CalendarComponentRef!: ViewContainerRef;
  @ViewChild('ProjectDetails', { static: true })
  ProjectDetailsTemplate!: TemplateRef<any>;
  editSeries = false; // Edit all recurring events
  addresses: { id: string | number; labels: string[] }[] = [];
  form;
  showExtraOptions = false;
  readonly InombraRoutes = InombraRoutes;
  readonly UploadSources = UploadSources;
  readonly ExtraOptionEnum = ExtraOptionEnum;
  searchTechnician;
  searchReminderTechnician;
  searchProjects;
  showTechniciansDropdown = false;
  showReminderTechniciansDropdown = false;
  countedFiles = 0;
  showMedia = false;
  processing = signal(false);
  extraOptions: ExtraOption[] = [];
  allowManualPeriodInput = false;

  get projectId() {
    if (this.isEditMode && !this.isTransformed) {
      return this.nzData.params[this.nzData.params.type];
    } else {
      return this.projects?.find(
        i => i.value?.id === this.form.get('project')?.value
      )?.value?.id;
    }
  }

  get projectCartType(): string {
    if (this.projectId && this.selectedProject?.value?.cart_type) {
      return this.selectedProject?.value?.cart_type;
    } else {
      return '';
    }
  }

  get cartTypeObject(): (typeof complexities)[number] | undefined {
    if (this.projectCartType) {
      return complexities.find(i => i.value === this.projectCartType);
    } else {
      return undefined;
    }
  }

  get cartTypeMessage() {
    const complexity = this.cartTypeObject;
    return `${complexity.label} Project: ${complexity.info}`;
  }

  get isEditMode() {
    return !!this.nzData.params.Guid;
  }

  get isTransformed(): boolean {
    if (this.form) {
      if (
        this.nzData.params.Id &&
        this.type === 'custom' &&
        this.form.get('eventType').value !== 'custom'
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  get eventType() {
    if (this.isEditMode && !this.isTransformed) {
      return this.nzData.params.type;
    } else {
      const projectId = this.form?.get('project').value;
      const eventType = this.form?.get('eventType').value;
      if (eventType === 'custom') {
        return 'custom';
      } else if (projectId) {
        const project = this.projects.find(i => i.value.id == projectId);
        return project.value.type;
      } else {
        return 'custom';
      }
    }
  }

  get type() {
    return this.nzData.params.type?.toLowerCase() || undefined;
  }

  get visibleAddressInput() {
    if (this.form.get('eventType').value === 'custom') {
      if (!this.isEditMode) {
        if (this.getExtraOptions.IsHoliday) {
          return false;
        } else {
          return true;
        }
      } else {
        if (this.nzData.params.IsHoliday || this.getExtraOptions.IsHoliday) {
          return false;
        } else {
          return true;
        }
      }
    } else {
      return false;
    }
  }

  get masterCustomerId() {
    return this.state?.customer?.id?.toString();
  }

  get hasExtraOptions(): boolean {
    return !!this.extraOptions.length;
  }

  get visibleProjectInput(): boolean {
    return (
      (this.form.get('eventType').value === 'project' && !this.isEditMode) ||
      (this.form.get('eventType').value === 'project' &&
        this.isEditMode &&
        this.isTransformed)
    );
  }

  get selectedProject() {
    if (
      this.projects.length &&
      this.form.get('eventType').value === 'project' &&
      this.projectId
    ) {
      return this.projects.find(i => i.value.id === this.projectId);
    }
  }

  projects = [];
  loadingProjects = signal(true);
  async getProjects() {
    this.loadingProjects.set(true);

    this.projects = this.schedulerService
      .processTaskOptions(
        await this.schedulerService.waitingProjects({
          action: '"book":true',
          customer: this.masterCustomerId,
        })
      )
      .sort((a, b) => {
        return (
          this.utilities.dateDiff(b.value.due_date, 'days') -
          this.utilities.dateDiff(a.value.due_date, 'days')
        );
      });

    if (this.nzData.preselectedProject) {
      this.form.patchValue({
        project: Number(this.nzData.preselectedProject),
      });
    }

    if (this.nzData.params.Draggable) {
      const draggableProject = this.projects.find(
        i => i.value.id === this.nzData.params[this.nzData.params.type]
      );
      if (draggableProject) {
        this.form.patchValue({
          project: draggableProject.value.id,
        });
      }
    }

    this.loadingProjects.set(false);
  }

  isCalendarVisible = false;
  schedulerPopoverCallback(event) {
    this.isCalendarVisible = false;
    this.form.patchValue({ reminderDate: event.startTime });
  }

  reminderPopoverVisibleChange(): void {
    if (this.isCalendarVisible) {
      this.initSchedulerPopover();
    }
  }

  private async initSchedulerPopover() {
    const { SchedulerPopoverComponent } = await import(
      '../scheduler-popover/scheduler-popover.component'
    );
    const calendarCompRef = this.CalendarComponentRef.createComponent(
      SchedulerPopoverComponent
    );

    calendarCompRef.setInput('view', 'Week');
    calendarCompRef.setInput('selectedDate', this.form.value.reminderDate);
    calendarCompRef.setInput(
      'technicians',
      this.form.value.reminderTechnicians
    );
    calendarCompRef.setInput('readonly', false);
    calendarCompRef.setInput('blockEvents', true);
    calendarCompRef.setInput('allowLegend', false);
    calendarCompRef.setInput('showHeaderBar', true);
    calendarCompRef.instance.clickEvent.subscribe(event => {
      this.schedulerPopoverCallback(event);
    });
  }

  ngAfterViewInit() {
    if (this.nzData.params.recurrenceRule) {
      this.ReccurenceEditor.set(this.nzData.params.recurrenceRule);
    }
  }

  daysToDeleteOptions = [
    {
      label: 'Half day',
      value: 0.5,
    },
    {
      label: 'One day',
      value: 1,
    },
    {
      label: 'Two days',
      value: 2,
    },
    {
      label: 'Three days',
      value: 3,
    },
  ];
  ngOnInit(): void {
    this.nzData.hideEventTypeSelection =
      this.nzData.hideEventTypeSelection || false;
    this.nzData.disableProjectSelection =
      this.nzData.disableProjectSelection || false;

    this.initForm(this.nzData.params);

    if (this.nzData.params.IsAllDay) {
      this.extraOptions.push(ExtraOptionEnum.IsAllDay);
    }
    if (this.nzData.params.IsHoliday) {
      this.extraOptions.push(ExtraOptionEnum.IsHoliday);
    }
    if (this.nzData.params.IsRecurring) {
      this.extraOptions.push(ExtraOptionEnum.IsRecurring);
    }
    if (this.nzData.params.IsReminder && !this.isEditMode) {
      this.extraOptions.push(ExtraOptionEnum.IsReminder);
    }
    if (this.nzData.params.IsFixedArrival) {
      this.extraOptions.push(ExtraOptionEnum.IsFixedArrival);
    }
    if (this.extraOptions.length) {
      this.showExtraOptions = true;
      this.resolveFormValidators();
    }
    if (this.nzData.params.days_to_delete) {
      this.extraOptions.push(ExtraOptionEnum.IsAutoDelete);
      if (
        !this.daysToDeleteOptions
          .map(i => i.value)
          .includes(this.nzData.params.days_to_delete)
      ) {
        this.allowManualPeriodInput = true;
      }
    }

    if (this.nzData.params.user_created) {
      const user = this.state.users.find(
        i => i.id == this.nzData.params.user_created
      );
      const date = new Date(this.nzData.params.date_created).toLocaleDateString(
        this.locale,
        { year: 'numeric', month: 'long', day: 'numeric' }
      );
      const time = new Date(this.nzData.params.date_created).toLocaleTimeString(
        this.locale
      );
      this.nzData.params.creation = `Created by ${user.fullName} on ${date} ${time}`;
    }

    this.getProjects();
    this.showExtraOptions = this.hasExtraOptions;
  }

  toggleExtra(option: ExtraOption): void {
    if (this.extraOptionExists(option)) {
      this.extraOptions.splice(this.extraOptions.indexOf(option), 1);
    } else {
      this.extraOptions.push(option);
    }
    this.resolveFormValidators();
  }

  extraOptionExists(option: ExtraOption): boolean {
    return this.extraOptions.includes(option);
  }

  extraOptionClass(option): 'primary' | 'default' {
    return this.extraOptionExists(option) ? 'primary' : 'default';
  }

  get getExtraOptions() {
    return {
      IsAllDay: this.allowExtraOption(ExtraOptionEnum.IsAllDay)
        ? this.extraOptionExists(ExtraOptionEnum.IsAllDay)
        : false,
      IsHoliday: this.allowExtraOption(ExtraOptionEnum.IsHoliday)
        ? this.extraOptionExists(ExtraOptionEnum.IsHoliday)
        : false,
      IsRecurring: this.allowExtraOption(ExtraOptionEnum.IsRecurring)
        ? this.extraOptionExists(ExtraOptionEnum.IsRecurring)
        : false,
      IsReminder: this.allowExtraOption(ExtraOptionEnum.IsReminder)
        ? this.extraOptionExists(ExtraOptionEnum.IsReminder)
        : false,
      IsFixedArrival: this.allowExtraOption(ExtraOptionEnum.IsFixedArrival)
        ? this.extraOptionExists(ExtraOptionEnum.IsFixedArrival)
        : false,
      IsAutoDelete: this.allowExtraOption(ExtraOptionEnum.IsAutoDelete)
        ? this.extraOptionExists(ExtraOptionEnum.IsAutoDelete)
        : false,
    };
  }

  allowExtraOption(option: ExtraOption) {
    // Another reminder creation is not allowed for reminder event
    if (this.nzData.params.IsReminder) {
      if (
        option === ExtraOptionEnum.IsAllDay ||
        option === ExtraOptionEnum.IsFixedArrival
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      if (this.form.get('eventType').value === 'custom') {
        return true;
      } else {
        if (option === ExtraOptionEnum.IsRecurring) {
          return false;
        } else if (option === ExtraOptionEnum.IsHoliday) {
          return false;
        } else if (option === ExtraOptionEnum.IsReminder) {
          return true;
        } else {
          return true;
        }
      }
    }
  }

  allowType(eventType): boolean {
    if (!this.isEditMode) {
      return true;
    } else {
      if (this.nzData.params.IsReminder) {
        return false;
      } else {
        if (this.type === 'custom') {
          return true;
        } else {
          if (this.type === eventType) {
            return true;
          } else {
            return false;
          }
        }
      }
    }
  }

  onDateChange(event) {
    const date = event ? [event.start, event.end] : null;
    if (date) {
      this.form.patchValue({ date: date });
    }
  }

  close(arg?: any) {
    this.modalRef.destroy(arg);
  }

  async save() {
    this.processing.set(true);
    const rawForm = this.form.getRawValue();
    const eventAddressId = this.getExtraOptions.IsHoliday
      ? null
      : await this._getAddressId({ ...this.addressFormEvent?.values });

    const reminderAddressId = this.getExtraOptions.IsReminder
      ? await this._createAddress({ ...this.addressFormReminder.values })
      : null;

    const payload = {
      Id: this.nzData.params?.Id || null,
      Subject: rawForm.subject,
      StartTime: moment(rawForm.date[0]).utc(false).toDate(),
      EndTime: moment(rawForm.date[1]).utc(false).toDate(),
      TechnicianId: rawForm.technicians,
      customEvent: this.eventType === 'custom' ? 'true' : 'false',
      type: this.eventType,
      cart: this._getEventModuleId('cart'),
      workflow: this._getEventModuleId('workflow'),
      account: this._getEventModuleId('account'),
      reference: this.nzData.params?.reference || null,
      address: eventAddressId,
      transformEvent: this.isTransformed.toString(),
      recurrenceRule: null,
      editMode: this.editSeries ? 'series' : 'current',
      reminder: {
        description: rawForm.reminderDescription,
        startDate: moment(rawForm.reminderDate).utc(false).toDate(),
        endDate: moment(rawForm.reminderDate)
          .utc(false)
          .add(2, 'hours')
          .toDate(),
        technicians: rawForm.reminderTechnicians,
      },
      reminderAddress: reminderAddressId,
      ...this.getExtraOptions,
      days_to_delete: this.extraOptionExists(ExtraOptionEnum.IsAutoDelete)
        ? Number(rawForm.daysToDelete)
        : null,
    };

    if (
      this.form.get('eventType').value === 'project' &&
      this.extraOptionExists(ExtraOptionEnum.IsRecurring)
    ) {
      payload.IsRecurring = false;
    } else if (this.extraOptionExists(ExtraOptionEnum.IsRecurring)) {
      payload.recurrenceRule = this.ReccurenceEditor.values;
    }

    let action = {
      action: 'batch',
      deleted: [],
      changed: [],
      added: [],
      params: {},
    };

    if (this.nzData.params.Id && !this.nzData.params.Draggable) {
      action.changed = [{ ...this.nzData.params, ...payload }];
    } else {
      action.added = [{ ...this.nzData.params, ...payload }];
      delete action.added[0].Id;
    }

    this.eventHttp.eventsUpdate(action).subscribe({
      next: res => {
        if (res.Id) {
          this.nzData.params.Id = res.Id;
        }

        const source_id =
          payload.type === 'custom'
            ? this.nzData.params.Id
            : payload[payload.type];
        const source_name =
          payload.type === 'custom'
            ? UploadSources.calendar
            : UploadSources[payload.type];

        this.uploadUpdateFiles(source_id, source_name).then(files => {
          this.processing.set(false);
          this.close(this.nzData.params);
        });
      },
      error: () => {
        this.notify.error(
          'Submission Failed',
          'Please review the form and correct the errors.'
        );
        this.processing.set(false);
      },
    });
  }

  uploadUpdateFiles(source_id, source_name) {
    return new Promise(resolve => {
      if (!this.UploadFilesComponent.files.length) {
        resolve([]);
      } else {
        this.UploadFilesComponent.source_id = source_id;
        this.UploadFilesComponent.source_name = source_name;
        this.UploadFilesComponent.uploadPromise().then(
          files => {
            resolve(files);
          },
          err => {}
        );
      }
    });
  }

  filelist = [];
  loadFiles(): void {
    if (this.form.get('eventType').value === 'custom') {
      if (this.nzData.params['Id']) {
        this.searchFiles(this.nzData.params['Id'], UploadSources.calendar);
      }
    } else {
      if (this.selectedProject) {
        this.searchFiles(
          this.selectedProject.value.id,
          UploadSources[this.selectedProject.type]
        );
      } else if (this.nzData.params['Id'] && !this.isTransformed) {
        this.searchFiles(
          this.nzData.params['ProjectId'],
          UploadSources[this.nzData.params['type']]
        );
      }
    }
  }

  fileSearchCriteria = {
    id: null,
    source: null,
  };
  loadingFiles = signal(false);
  searchFiles(id, source): void {
    if (
      id &&
      source &&
      (this.fileSearchCriteria.id !== id ||
        this.fileSearchCriteria.source !== source)
    ) {
      this.loadingFiles.set(true);
      this.filelist = [];
      this.fileSearchCriteria = { id, source };

      this.fileHttp
        .searchFiles({
          sources: [source],
          source_ids: [id],
        })
        .subscribe(res => {
          this.filelist = res.files;
          this.loadingFiles.set(false);
        });
    }
  }

  get utcOffset() {
    return moment().utcOffset();
  }

  initForm(data?) {
    let startTime, endTime, reminderDate;

    if (this.isEditMode) {
      startTime = moment(data.StartTime)
        .add(this.utcOffset, 'minutes')
        .format(dateTimeFormat);
      endTime = moment(data.EndTime)
        .add(this.utcOffset, 'minutes')
        .format(dateTimeFormat);
      reminderDate = moment(data?.reminderEvents?.startTime)
        .add(this.utcOffset, 'minutes')
        .format(dateTimeFormat);
    } else {
      startTime = moment(data.StartTime).format(dateTimeFormat);
      endTime = moment(data.StartTime).add(2, 'hours').format(dateTimeFormat);
      reminderDate = moment(startTime)
        .subtract(2, 'hours')
        .format(dateTimeFormat);
    }

    if (data.reminderEvents && Object.keys(data.reminderEvents).length) {
      this.extraOptions.push(ExtraOptionEnum.IsReminder);
    }

    this.form = this.fb.group({
      eventType: [
        data.type === 'custom' ? 'custom' : 'project',
        Validators.required,
      ],
      subject: [this.isEditMode ? data.Subject : ''],
      date: [[startTime, endTime], Validators.required],
      technicians: [
        this.isEditMode ? data.TechnicianId : [],
        Validators.required,
      ],
      project: [this.isEditMode ? data[data.type] : null, Validators.required],
      reminderTechnicians: [data?.reminderEvents?.technicians || null],
      reminderDate: [reminderDate],
      reminderDescription: [data?.reminderEvents?.Subject || null],
      daysToDelete: [data.days_to_delete ?? 0, null],
    });

    this.form.get('eventType').valueChanges.subscribe(value => {
      this.resolveFormValidators();
      this.loadFiles();
    });

    this.form.get('technicians').valueChanges.subscribe(value => {
      this.showTechniciansDropdown = false;
    });

    this.form.get('reminderTechnicians').valueChanges.subscribe(value => {
      this.showReminderTechniciansDropdown = false;
    });

    this.form.get('project').valueChanges.subscribe(value => {
      this.loadFiles();
    });

    this.resolveFormValidators();
  }

  resolveFormValidators() {
    this.formValidatorForEventType();
    this.formValidatorForReminder();
    this.formValidatorForAutoDelete();

    this.form.controls['subject'].updateValueAndValidity();
    this.form.controls['project'].updateValueAndValidity();
  }

  formValidatorForEventType(): void {
    if (this.form.get('eventType').value === 'custom') {
      this.form.controls['subject'].setValidators([Validators.required]);
      this.form.controls['project'].setValidators([]);
    } else {
      this.form.controls['subject'].setValidators([]);
      this.form.controls['project'].setValidators([Validators.required]);
    }
  }

  formValidatorForReminder(): void {
    const validators = this.getExtraOptions.IsReminder
      ? [Validators.required]
      : [];
    this.form.controls['reminderDescription'].setValidators(validators);
    this.form.controls['reminderTechnicians'].setValidators(validators);
    this.form.controls['reminderDescription'].updateValueAndValidity();
    this.form.controls['reminderTechnicians'].updateValueAndValidity();
  }

  formValidatorForAutoDelete(): void {
    const validators = this.getExtraOptions.IsAutoDelete
      ? [Validators.required, Validators.min(0.5)]
      : [];
    this.form.controls['daysToDelete'].setValidators(validators);
    this.form.controls['daysToDelete'].updateValueAndValidity();
  }

  deleteEvent() {
    this.modalService
      .create({
        nzContent: DeleteEventModalComponent,
        nzClosable: false,
        nzMaskClosable: false,
        nzCentered: true,
        nzData: this.nzData.params,
      })
      .afterClose.subscribe(result => {
        if (result) {
          this.close(true);
        }
      });
  }

  mapAddress(id?) {
    return id
      ? this.utilities.formatAddress(this._task, {
          postcode: 'delivery_postcode',
          address1: 'delivery_address1',
          address2: 'delivery_address2',
          city: 'delivery_city',
        })
      : this.utilities.formatAddress(this.nzData?.params);
  }

  openMap(id?) {
    const address = this.mapAddress(id);
    window.open(`https://maps.google.com/?q=${address}`, '_blank');
  }

  openJob(id?) {
    if (id) {
      const route = this.eventType;
      window.open(`/${route}/${id}`, '_blank');
    } else {
      const type = this.nzData.params.type;
      const id = this.nzData.params[type];
      window.open(`/${type}/${id}`, '_blank');
    }
  }

  public get valid(): boolean {
    if (!this.form?.valid) {
      return false;
    }
    const childForms = [this.addressFormEvent, this.addressFormReminder].filter(
      f => !!f
    );
    return childForms.every(f => f.valid);
  }

  private _getEventModuleId(type) {
    if (this.eventType === type) {
      return this.projectId;
    } else {
      return 0;
    }
  }

  private get _task() {
    // Tasks can be empty because when they are booked
    // they dissapear from tasklist, but the values can be
    // found on event object
    const project = this.projects.find(
      i => i.value.id === this.form.get('project').value
    );
    if (!project) {
      return { address_id: this.nzData.params.address };
    } else {
      return project.value;
    }
  }

  private _getAddressId(payload) {
    return new Promise(resolve => {
      if (this.visibleAddressInput) {
        this._createAddress(payload).then(resolve);
      } else {
        if (
          this.nzData.params.address &&
          this.form.get('eventType').value === 'custom'
        ) {
          resolve(this.nzData.params.address.toString());
        } else {
          resolve(this._task.address_id.toString());
        }
      }
    });
  }

  private _createAddress(payload) {
    return new Promise(resolve => {
      this.addressHttp.createAddress(payload).subscribe({
        next: res => resolve(res.id),
        error: () => resolve(null),
      });
    });
  }
}
